import axios from 'axios'
import { ActionTree } from 'vuex'
import _ from 'lodash'
import { RootState } from '@/store/types'
import {
  Mail,
  MailForRegisterToStudent,
  MailForRegisterToTutor,
  MailsConditions,
  MailsState,
  Receiver,
} from '@/store/mails/types'
import { getPagerFromApiResponse } from '@/store/utils'
import moment from 'moment'

const actions: ActionTree<MailsState, RootState> = {
  /**
   * メール一覧取得
   * @param {{
   *  conditions: MailsConditions, // 検索条件
    * page: number, // ページ番号
   * }}
   */
  fetchMails: async (
    { commit },
    { conditions, page }: { conditions?: MailsConditions, page: number },
  ) => {
    const { headers, data } = await axios.get('/mails', {
      params: {
        page,
        ..._.pickBy({
          subject: conditions?.subject,
          status: conditions?.statuses.join(','),
          universityCode: conditions?.university?.code,
          sendDateFrom: conditions?.from?.format('YYYYMMDD'),
          sendDateTo: conditions?.to?.format('YYYYMMDD'),
          targetType: conditions?.target?.type,
          tutorId: (conditions?.target?.type === 'tutor') && conditions.target.word,
          studentNumber: (conditions?.target?.type === 'student') && conditions.target.word,
          limit: 50,
        }),
      },
    })

    const mails: Mail[] = _.map(data, (d): Mail => ({
      id: d.notificationId,
      subject: d.subject,
      status: d.sendStatusName,
      sendAt: moment(d.sendDate, 'YYYYMMDDHHmm'),
    }))

    commit('fetchedMails', {
      pager: getPagerFromApiResponse(page, headers),
      mails,
    })
  },

  /**
   * メールを取得
   * @param {{
   *  notificationId: string, // 通知ID
   * }}
   */
  getMail: async (
    { commit },
    { notificationId }: { notificationId: string },
  ) => {
    const { data } = await axios.get(`/mails/${notificationId}`)

    const mail: Mail = {
      id: notificationId,
      status: data.sendStatusName,
      subject: data.subject,
      sendAt: moment(data.sendDate, 'YYYYMMDDHHmm'),
      body: data.body,
      receivers: _.map(data.receiver, (r): Receiver => {
        if (r.tutorId !== null) {
          return {
            type: 'tutor',
            id: r.tutorId,
            name: r.name,
          }
        }

        if (r.studentId !== null) {
          return {
            type: 'student',
            id: r.studentId,
            name: r.name,
          }
        }

        throw Error(`メールのreceiverに設定された送信対象者がわかりません, ${JSON.stringify(r)}`)
      }),
    }

    commit('gotMail', mail)
  },

  /**
   * チューター一括メール登録
   * @param context
   * @param mail {MailForRegisterToTutor}
   */
  registerMailToTutor: async (
    context,
    mail: MailForRegisterToTutor,
  ) => axios.post('/mails/tutors', _.pickBy({
    tutor: mail.tutorConditions?.tutorNameOrId,
    positionCode: mail.tutorConditions?.positions?.map(({ code }) => code).join(','),
    nationalityCode: mail.tutorConditions?.nationality?.code,
    includeTerminationOfService: mail.tutorConditions?.isIncludeTerminationOfService ? 1 : 0,
    gender: mail.tutorConditions?.genderCodes?.join(','),
    authority: mail.tutorConditions?.authorities?.join(','),
    sendDate: mail.sendDate?.format('YYYYMMDDHHmm'),
    subject: mail.subject,
    body: mail.body,
  })),

  /**
   * 履修生一括メール登録
   * @param context
   * @param mail {MailForRegisterToStudent}
   */
  registerMailToStudent: async (
    context,
    mail: MailForRegisterToStudent,
  ) => axios.post('/mails/students', _.pickBy({
    studentIds: mail.studentConditions?.studentIds?.join(','),
    studentNumber: mail.studentConditions?.studentNumber,
    oldStudentNumber: mail.studentConditions?.oldStudentNumber,
    name: mail.studentConditions?.studentNameJapanese,
    nameEn: mail.studentConditions?.studentName,
    email: mail.studentConditions?.email,
    universityCode: mail.studentConditions?.university?.code,
    campusCodes: mail.studentConditions?.campuses?.map(({ code }) => code).join(','),
    fiscalYear: mail.studentConditions?.fiscalYear,
    semesterCode: mail.studentConditions?.semester?.code,
    dayOfWeeks: mail.studentConditions?.dayOfWeeks?.join(','),
    courseName: mail.studentConditions?.courseName,
    levelCodes: mail.studentConditions?.levels?.map(({ code }) => code).join(','),
    room: mail.studentConditions?.classroom,
    boothNumber: mail.studentConditions?.booth,
    periods: mail.studentConditions?.periods?.join(','),
    tutorId: mail.studentConditions?.tutorId,
    tutorName: mail.studentConditions?.tutorName,
    specialConsiderationCode: mail.studentConditions?.specialConsideration?.code,
    hasSpir: mail.studentConditions?.isSpir ? 1 : null,
    hasInquiry: mail.studentConditions?.isChangeGrade ? 1 : null,
    wetecId: mail.studentConditions?.wetecId,
    hasNotWetecId: mail.studentConditions?.hasNotWetecId,
    absencesCount: mail.studentConditions?.absencesCount,
    sendDate: mail.sendDate?.format('YYYYMMDDHHmm'),
    subject: mail.subject,
    body: mail.body,
  })),

  /**
   * メールの送信をキャンセルする
   * @param {{
   *  notificationId: string, // キャンセルするメールのID
   * }}
   * @returns {Promise<void>}
   */
  cancelMail: async (
    context,
    { notificationId }: { notificationId: string },
  ) => axios.put(`/mails/${notificationId}/cancel`),
}

export default actions
